import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAttendance, getTodayAttendance, checkIn, checkOut, getLocations } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
    Clock,
    Camera,
    SignIn,
    SignOut,
    MapPin,
    User,
    CalendarBlank,
    CheckCircle
} from '@phosphor-icons/react';

const AttendancePage = () => {
    const { user, isSeller } = useAuth();
    const [attendance, setAttendance] = useState([]);
    const [todayRecord, setTodayRecord] = useState(null);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCheckIn, setShowCheckIn] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState('');
    const [capturedPhoto, setCapturedPhoto] = useState(null);
    const [capturing, setCapturing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [attendanceRes, todayRes, locationsRes] = await Promise.all([
                getAttendance(),
                getTodayAttendance(),
                getLocations()
            ]);
            setAttendance(attendanceRes.data);
            setTodayRecord(todayRes.data);
            setLocations(locationsRes.data);
            
            // Pre-select user's location
            if (user?.location_id) {
                setSelectedLocation(user.location_id);
            } else if (locationsRes.data.length > 0) {
                setSelectedLocation(locationsRes.data[0].id);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const startCamera = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'user', width: 640, height: 480 }
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
            setCapturing(true);
        } catch (error) {
            toast.error('No se pudo acceder a la cámara');
            console.error('Camera error:', error);
        }
    }, []);

    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        setCapturing(false);
    }, []);

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            context.drawImage(video, 0, 0);
            
            const photoData = canvas.toDataURL('image/jpeg', 0.7);
            setCapturedPhoto(photoData);
            stopCamera();
        }
    };

    const retakePhoto = () => {
        setCapturedPhoto(null);
        startCamera();
    };

    const handleOpenCheckIn = () => {
        setShowCheckIn(true);
        setCapturedPhoto(null);
        setTimeout(() => startCamera(), 100);
    };

    const handleCloseCheckIn = () => {
        stopCamera();
        setCapturedPhoto(null);
        setShowCheckIn(false);
    };

    const handleCheckIn = async () => {
        if (!capturedPhoto) {
            toast.error('Toma una foto para registrar asistencia');
            return;
        }
        
        if (!selectedLocation) {
            toast.error('Selecciona una ubicación');
            return;
        }
        
        setSubmitting(true);
        try {
            await checkIn({
                seller_id: user.id,
                location_id: selectedLocation,
                photo_data: capturedPhoto
            });
            toast.success('Registro de entrada exitoso');
            handleCloseCheckIn();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al registrar entrada');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCheckOut = async () => {
        if (!todayRecord) return;
        
        try {
            await checkOut(todayRecord.id);
            toast.success('Salida registrada');
            fetchData();
        } catch (error) {
            toast.error('Error al registrar salida');
        }
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleTimeString('es-MX', {
            timeZone: 'America/Mexico_City',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('es-MX', {
            timeZone: 'America/Mexico_City',
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const getLocationName = (locationId) => {
        const location = locations.find(l => l.id === locationId);
        return location?.name || '-';
    };

    const calculateHours = (checkInTime, checkOutTime) => {
        if (!checkInTime || !checkOutTime) return '-';
        const diff = new Date(checkOutTime) - new Date(checkInTime);
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours}h ${minutes}m`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F2D4B]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="attendance-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-editorial text-2xl sm:text-3xl font-medium text-[#1F2D4B]">
                        Control de Asistencia
                    </h1>
                    <p className="text-[#4B5878] mt-1">
                        Registro de entrada y salida de vendedores
                    </p>
                </div>
            </div>

            {/* Today's Status Card */}
            {isSeller() && (
                <Card className="bg-white border-[#ECE3D2] rounded-2xl">
                    <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                                    todayRecord && !todayRecord.check_out_time
                                        ? 'bg-[#D1FAE5]'
                                        : 'bg-[#F8F4EC]'
                                }`}>
                                    <Clock 
                                        size={32} 
                                        weight="duotone" 
                                        className={todayRecord && !todayRecord.check_out_time 
                                            ? 'text-[#059669]' 
                                            : 'text-[#4B5878]'
                                        } 
                                    />
                                </div>
                                <div>
                                    <h2 className="font-editorial text-lg font-medium text-[#1F2D4B]">
                                        Estado de Hoy
                                    </h2>
                                    {todayRecord ? (
                                        <div className="text-sm text-[#4B5878]">
                                            <p>Entrada: {formatTime(todayRecord.check_in_time)}</p>
                                            {todayRecord.check_out_time && (
                                                <p>Salida: {formatTime(todayRecord.check_out_time)}</p>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-[#4B5878]">No has registrado entrada</p>
                                    )}
                                </div>
                            </div>
                            
                            <div className="flex gap-3">
                                {!todayRecord ? (
                                    <Button
                                        onClick={handleOpenCheckIn}
                                        data-testid="check-in-btn"
                                        className="h-12 px-6 bg-[#059669] hover:bg-[#1E4D38] text-white rounded-xl"
                                    >
                                        <SignIn size={20} className="mr-2" />
                                        Registrar Entrada
                                    </Button>
                                ) : !todayRecord.check_out_time ? (
                                    <Button
                                        onClick={handleCheckOut}
                                        data-testid="check-out-btn"
                                        className="h-12 px-6 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl"
                                    >
                                        <SignOut size={20} className="mr-2" />
                                        Registrar Salida
                                    </Button>
                                ) : (
                                    <Badge className="bg-[#D1FAE5] text-[#059669] border border-[#059669]/20 h-12 px-6 flex items-center gap-2">
                                        <CheckCircle size={18} weight="fill" />
                                        Jornada Completada
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Attendance History */}
            <Card className="bg-white border-[#ECE3D2] rounded-2xl overflow-hidden">
                <CardHeader className="border-b border-[#ECE3D2] bg-[#F8F4EC]">
                    <CardTitle className="font-editorial text-lg text-[#1F2D4B] flex items-center gap-2">
                        <CalendarBlank size={20} weight="duotone" className="text-[#FB7185]" />
                        Historial de Asistencias
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="hover:bg-transparent">
                                    <TableHead className="font-semibold text-[#1F2D4B]">Fecha</TableHead>
                                    {!isSeller() && (
                                        <TableHead className="font-semibold text-[#1F2D4B]">Vendedor</TableHead>
                                    )}
                                    <TableHead className="font-semibold text-[#1F2D4B]">Ubicación</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Entrada</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Salida</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Horas</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Foto</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {attendance.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={isSeller() ? 6 : 7} className="text-center py-8 text-[#4B5878]">
                                            <Clock size={40} weight="duotone" className="mx-auto mb-2 opacity-50" />
                                            <p>No hay registros de asistencia</p>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    attendance.map((record) => (
                                        <TableRow key={record.id} className="hover:bg-[#F8F4EC]/50">
                                            <TableCell className="font-medium">
                                                {formatDate(record.check_in_time)}
                                            </TableCell>
                                            {!isSeller() && (
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-8 h-8 rounded-full bg-[#1F2D4B] flex items-center justify-center text-white text-xs font-medium">
                                                            {record.seller?.name?.charAt(0).toUpperCase()}
                                                        </div>
                                                        <span>{record.seller?.name}</span>
                                                    </div>
                                                </TableCell>
                                            )}
                                            <TableCell className="text-[#4B5878]">
                                                {getLocationName(record.location_id)}
                                            </TableCell>
                                            <TableCell className="text-[#059669] font-medium">
                                                {formatTime(record.check_in_time)}
                                            </TableCell>
                                            <TableCell className={record.check_out_time ? 'text-[#FB7185] font-medium' : 'text-[#4B5878]'}>
                                                {record.check_out_time ? formatTime(record.check_out_time) : 'En turno'}
                                            </TableCell>
                                            <TableCell>
                                                {calculateHours(record.check_in_time, record.check_out_time)}
                                            </TableCell>
                                            <TableCell>
                                                {record.photo_data && (
                                                    <img 
                                                        src={record.photo_data} 
                                                        alt="Check-in" 
                                                        className="w-10 h-10 rounded-lg object-cover"
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Check-in Modal */}
            <Dialog open={showCheckIn} onOpenChange={handleCloseCheckIn}>
                <DialogContent className="sm:max-w-md rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-editorial text-xl text-[#1F2D4B] flex items-center gap-2">
                            <Camera size={24} weight="duotone" className="text-[#FB7185]" />
                            Registrar Entrada
                        </DialogTitle>
                        <DialogDescription>
                            Toma una foto para verificar tu asistencia
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        {/* Location Select */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-[#1F2D4B] flex items-center gap-2">
                                <MapPin size={16} />
                                Ubicación
                            </label>
                            <Select
                                value={selectedLocation}
                                onValueChange={setSelectedLocation}
                            >
                                <SelectTrigger data-testid="attendance-location-select" className="h-12 rounded-xl border-[#ECE3D2]">
                                    <SelectValue placeholder="Seleccionar ubicación" />
                                </SelectTrigger>
                                <SelectContent>
                                    {locations.map((location) => (
                                        <SelectItem key={location.id} value={location.id}>
                                            {location.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Camera/Photo Area */}
                        <div className="relative aspect-[4/3] bg-[#F0EDE6] border border-[#ECE3D2] rounded-xl overflow-hidden">
                            {capturing && !capturedPhoto && (
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    className="w-full h-full object-cover"
                                />
                            )}
                            {capturedPhoto && (
                                <img 
                                    src={capturedPhoto} 
                                    alt="Captured" 
                                    className="w-full h-full object-cover"
                                />
                            )}
                            {!capturing && !capturedPhoto && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="text-center text-[#4B5878]">
                                        <Camera size={48} className="mx-auto mb-2 opacity-50" />
                                        <p className="text-sm opacity-70">Cargando cámara...</p>
                                    </div>
                                </div>
                            )}
                            <canvas ref={canvasRef} className="hidden" />
                        </div>

                        {/* Camera Controls */}
                        <div className="flex gap-3">
                            {!capturedPhoto ? (
                                <Button
                                    onClick={capturePhoto}
                                    disabled={!capturing}
                                    data-testid="capture-photo-btn"
                                    className="flex-1 h-12 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl"
                                >
                                    <Camera size={20} className="mr-2" />
                                    Tomar Foto
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        variant="outline"
                                        onClick={retakePhoto}
                                        className="flex-1 h-12 rounded-xl border-[#ECE3D2]"
                                    >
                                        Retomar
                                    </Button>
                                    <Button
                                        onClick={handleCheckIn}
                                        disabled={submitting}
                                        data-testid="confirm-checkin-btn"
                                        className="flex-1 h-12 bg-[#059669] hover:bg-[#1E4D38] text-white rounded-xl"
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                Registrando...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle size={20} className="mr-2" />
                                                Confirmar Entrada
                                            </>
                                        )}
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AttendancePage;
